// Berichte (Proof of Play, Erweiterung): Was lief wann, wie oft und wie lange auf welchem Bildschirm.
// Reiter: Übersicht (Kennzahlen, Diagramme, Tabelle, Export), Kampagne (Nachweis für ein Medium), Einstellungen.
// Filter und Zeitraum stehen in der Adresse (teilbar, Link aus dem Monatsbericht).
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { can, useMe } from '../api/queries';
import {
  exportUrl, getMediaPlays, playsExportUrl, rk, useReport, useReportOptions, useReportSettings, type PlayRow, type ReportFilter, type ReportOptions,
  type ReportSettings,
} from '../api/reports';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Empty, Field, PageHead, Spinner } from '../components/ui';
import '../styles/reports.css';

type Tab = 'overview' | 'campaign' | 'settings';
type Preset = '7d' | '30d' | 'month' | 'lastMonth' | 'custom';

// ---------- Datum und Formatierung ----------

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export function presetRange(p: Exclude<Preset, 'custom'>, today = new Date()): { from: string; to: string } {
  if (p === '7d') return { from: ymd(addDays(today, -6)), to: ymd(today) };
  if (p === '30d') return { from: ymd(addDays(today, -29)), to: ymd(today) };
  if (p === 'month') return { from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(today) };
  return { from: ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)), to: ymd(new Date(today.getFullYear(), today.getMonth(), 0)) };
}

const nf = (lang: string) => new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'de-CH');
/** Spielzeit lesbar: „3 Std. 05 Min.“ / „12 Min. 30 s“ / „45 s“. */
export function playTime(ms: number, lang: string): string {
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const [H, M, S] = lang === 'en' ? ['h', 'min', 's'] : ['Std.', 'Min.', 's'];
  if (h) return `${nf(lang).format(h)} ${H} ${pad(m)} ${M}`;
  if (m) return `${m} ${M} ${pad(sec)} ${S}`;
  return `${sec} ${S}`;
}
const pct = (v: number, lang: string) => `${(v * 100).toLocaleString(lang === 'en' ? 'en-GB' : 'de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
const dateLabel = (d: string, lang: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString(lang === 'en' ? 'en-GB' : 'de-CH', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
const dateLong = (d: string, lang: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString(lang === 'en' ? 'en-GB' : 'de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
const localTime = (iso: string, tz: string, lang: string) => {
  try {
    return new Date(iso).toLocaleString(lang === 'en' ? 'en-GB' : 'de-CH', { timeZone: tz, dateStyle: 'medium', timeStyle: 'medium' });
  } catch {
    return iso;
  }
};

// ---------- Seite ----------

export function ReportsPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const canSettings = can(me, 'tenant.settings.manage');
  const tabParam = params.get('tab');
  const tab: Tab = tabParam === 'campaign' ? 'campaign' : tabParam === 'settings' ? 'settings' : 'overview';
  const setTab = (k: Tab) => {
    const p = new URLSearchParams(params);
    if (k === 'overview') p.delete('tab');
    else p.set('tab', k);
    setParams(p, { replace: true });
  };
  return (
    <>
      <PageHead title={t('reports.title')} sub={t('reports.sub')} />
      <div className="tabs" role="tablist">
        {(['overview', 'campaign', 'settings'] as Tab[]).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{t(`reports.tab.${k}`)}</button>
        ))}
      </div>
      {tab === 'settings' ? <SettingsTab canEdit={canSettings} /> : <ReportView campaign={tab === 'campaign'} />}
    </>
  );
}

/** Zeitraum und Filter aus der Adresse. */
function useFilter(): { f: ReportFilter; preset: Preset; set: (patch: Record<string, string | null>) => void } {
  const [params, setParams] = useSearchParams();
  const preset = (params.get('range') as Preset | null) ?? (params.get('from') ? 'custom' : '30d');
  const range = preset === 'custom' ? { from: params.get('from') ?? presetRange('30d').from, to: params.get('to') ?? presetRange('30d').to } : presetRange(preset);
  const f: ReportFilter = { ...range };
  for (const k of ['locationId', 'groupId', 'deviceId', 'playlistId', 'mediaId'] as const) {
    const v = params.get(k);
    if (v) f[k] = v;
  }
  const set = (patch: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };
  return { f, preset, set };
}

function Filters({ f, preset, set, options, campaign }: { f: ReportFilter; preset: Preset; set: (p: Record<string, string | null>) => void; options: ReportOptions; campaign: boolean }) {
  const { t } = useTranslation();
  const devices = f.locationId ? options.devices.filter((d) => d.locationId === f.locationId) : options.devices;
  return (
    <div className="toolbar report-filters">
      <select aria-label={t('reports.filter.range')} value={preset} onChange={(e) => {
        const v = e.target.value as Preset;
        set(v === 'custom' ? { range: 'custom', from: f.from, to: f.to } : { range: v, from: null, to: null });
      }}>
        {(['7d', '30d', 'month', 'lastMonth', 'custom'] as Preset[]).map((p) => <option key={p} value={p}>{t(`reports.range.${p}`)}</option>)}
      </select>
      {preset === 'custom' && (
        <>
          <input type="date" aria-label={t('reports.filter.from')} value={f.from} max={f.to} onChange={(e) => e.target.value && set({ range: 'custom', from: e.target.value, to: f.to })} />
          <input type="date" aria-label={t('reports.filter.to')} value={f.to} min={f.from} onChange={(e) => e.target.value && set({ range: 'custom', from: f.from, to: e.target.value })} />
        </>
      )}
      <select aria-label={t('reports.filter.location')} value={f.locationId ?? ''} onChange={(e) => set({ locationId: e.target.value, deviceId: null })}>
        <option value="">{t('reports.filter.allLocations')}</option>
        {options.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
      {options.groups.length > 0 && (
        <select aria-label={t('reports.filter.group')} value={f.groupId ?? ''} onChange={(e) => set({ groupId: e.target.value })}>
          <option value="">{t('reports.filter.allGroups')}</option>
          {options.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      )}
      <select aria-label={t('reports.filter.screen')} value={f.deviceId ?? ''} onChange={(e) => set({ deviceId: e.target.value })}>
        <option value="">{t('reports.filter.allScreens')}</option>
        {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>
      <select aria-label={t('reports.filter.playlist')} value={f.playlistId ?? ''} onChange={(e) => set({ playlistId: e.target.value })}>
        <option value="">{t('reports.filter.allPlaylists')}</option>
        {options.playlists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select aria-label={t('reports.filter.media')} value={f.mediaId ?? ''} onChange={(e) => set({ mediaId: e.target.value })} className={campaign && !f.mediaId ? 'need' : ''}>
        <option value="">{campaign ? t('reports.campaign.choose') : t('reports.filter.allMedia')}</option>
        {options.media.map((m) => <option key={m.id} value={m.id}>{m.deleted ? `${m.name} (${t('reports.deleted')})` : m.name}</option>)}
      </select>
    </div>
  );
}

function ReportView({ campaign }: { campaign: boolean }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'de';
  const { f, preset, set } = useFilter();
  const options = useReportOptions();
  const report = useReport(f, !campaign || !!f.mediaId);
  // beim Filtern den bisherigen Bericht stehen lassen, bis der neue da ist (kein Flackern)
  const last = useRef(report.data);
  if (report.data) last.current = report.data;
  if (options.isLoading || !options.data) return <Spinner />;
  const r = report.data ?? (report.isFetching ? last.current : undefined);
  const exports = (
    <div className="row report-exports">
      <a className="btn" href={campaign && f.mediaId ? playsExportUrl(f.mediaId, { ...f, mediaId: undefined } as ReportFilter, 'csv', lang) : exportUrl(f, 'csv', lang)} download data-testid="export-csv">
        <Icon name="download" />{t('reports.export.csv')}
      </a>
      <a className="btn" href={campaign && f.mediaId ? playsExportUrl(f.mediaId, { ...f, mediaId: undefined } as ReportFilter, 'pdf', lang) : exportUrl(f, 'pdf', lang)} download data-testid="export-pdf">
        <Icon name="download" />{t('reports.export.pdf')}
      </a>
    </div>
  );
  return (
    <>
      <Filters f={f} preset={preset} set={set} options={options.data} campaign={campaign} />
      {campaign && !f.mediaId ? (
        <Empty icon="image" title={t('reports.campaign.emptyTitle')} text={options.data.media.length ? t('reports.campaign.emptyText') : t('reports.noMedia')} />
      ) : !r ? <Spinner /> : (
        <div className="col report" style={{ gap: 18, opacity: report.data ? 1 : 0.6, transition: 'opacity .15s' }}>
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div className="muted small">{t('reports.period', { from: dateLong(r.from, lang), to: dateLong(r.to, lang), tz: options.data.timezone })}</div>
            {exports}
          </div>
          <Kpis r={r} lang={lang} contentFilter={!!(f.mediaId || f.playlistId)} />
          {r.totals.plays === 0 ? (
            <Empty icon="calendar" title={t('reports.empty.title')} text={t('reports.empty.text')} />
          ) : campaign && f.mediaId ? (
            <>
              <div className="card">
                <h2 className="chart-title">{t('reports.chart.perDay')}</h2>
                <ColumnChart data={r.byDay.map((d) => ({ key: d.date, label: dateLabel(d.date, lang), value: d.plays, tip: `${dateLong(d.date, lang)}: ${t('reports.playsN', { count: d.plays })} · ${playTime(d.durationMs, lang)}` }))} lang={lang} />
              </div>
              <PlaysList mediaId={f.mediaId} f={f} lang={lang} rawSince={r.rawSince} />
            </>
          ) : (
            <>
              <div className="grid-2">
                <div className="card">
                  <h2 className="chart-title">{t('reports.chart.perDay')}</h2>
                  <ColumnChart data={r.byDay.map((d) => ({ key: d.date, label: dateLabel(d.date, lang), value: d.plays, tip: `${dateLong(d.date, lang)}: ${t('reports.playsN', { count: d.plays })} · ${playTime(d.durationMs, lang)}` }))} lang={lang} />
                </div>
                <div className="card">
                  <h2 className="chart-title">{t('reports.chart.perHour')}</h2>
                  <ColumnChart data={r.byHour.map((h) => ({ key: String(h.hour), label: String(h.hour), value: h.plays, tip: `${pad(h.hour)}:00–${pad(h.hour)}:59: ${t('reports.playsN', { count: h.plays })} · ${playTime(h.durationMs, lang)}` }))} lang={lang} every={3} />
                </div>
              </div>
              <div className="grid-2">
                <div className="card">
                  <h2 className="chart-title">{t('reports.chart.top')}</h2>
                  <BarList rows={r.topContent.slice(0, 10).map((c, i) => ({
                    key: `${c.type}:${c.id ?? i}`, label: c.deleted ? `${c.name} (${t('reports.deleted')})` : c.name,
                    sub: `${t(`reports.type.${c.type}`)}${c.zonePlays ? ` · ${t('reports.zonePlays', { count: c.zonePlays })}` : ''}`,
                    value: c.durationMs, display: playTime(c.durationMs, lang), extra: `${nf(lang).format(c.plays)}× · ${pct(c.share, lang)}`,
                    action: c.type === 'media' && c.id ? () => set({ tab: 'campaign', mediaId: c.id }) : undefined,
                  }))} actionLabel={t('reports.campaign.open')} />
                </div>
                <div className="card">
                  <h2 className="chart-title">{t('reports.chart.screens')}</h2>
                  <BarList rows={r.screens.slice(0, 10).map((s) => ({
                    key: s.deviceId, label: s.name, sub: s.locationName ?? '', value: s.durationMs, display: playTime(s.durationMs, lang),
                    extra: `${nf(lang).format(s.plays)}× · ${pct(s.share, lang)}`,
                  }))} />
                </div>
              </div>
              <DetailTable r={r} lang={lang} />
            </>
          )}
        </div>
      )}
    </>
  );
}

function Kpis({ r, lang, contentFilter }: { r: NonNullable<ReturnType<typeof useReport>['data']>; lang: string; contentFilter: boolean }) {
  const { t } = useTranslation();
  const tot = r.totals;
  const notes = [
    tot.interrupted ? t('reports.kpi.interrupted', { count: tot.interrupted }) : '',
    tot.displaced ? t('reports.kpi.displaced', { count: tot.displaced }) : '',
    tot.failed ? t('reports.kpi.failed', { count: tot.failed }) : '',
  ].filter(Boolean);
  const kpi = (icon: string, label: string, value: string, hint?: string, testId?: string): ReactNode => (
    <div className="card stat" data-testid={testId}>
      <div className="ic"><Icon name={icon} /></div>
      <div><div className="v">{value}</div><div className="muted small">{label}</div>{hint && <div className="muted small kpi-hint">{hint}</div>}</div>
    </div>
  );
  return (
    <>
      <div className="stats" style={{ marginBottom: 0 }}>
        {kpi('play', t('reports.kpi.plays'), nf(lang).format(tot.plays), undefined, 'kpi-plays')}
        {kpi('clock', t('reports.kpi.time'), playTime(tot.durationMs, lang), undefined, 'kpi-time')}
        {kpi('grid', t('reports.kpi.share'), pct(tot.share, lang), contentFilter ? t('reports.kpi.shareOf', { time: playTime(tot.screenTimeMs, lang) }) : t('reports.kpi.shareAll'), 'kpi-share')}
        {kpi('screen', t('reports.kpi.screens'), nf(lang).format(tot.screens), undefined, 'kpi-screens')}
      </div>
      {notes.length > 0 && <div className="muted small">{notes.join(' · ')}</div>}
    </>
  );
}

// ---------- Diagramme (eine Reihe, ohne Legende; Werte per Hover/Fokus) ----------

interface Col { key: string; label: string; value: number; tip: string }

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}

export function ColumnChart({ data, lang, every }: { data: Col[]; lang: string; every?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...data.map((d) => d.value)));
  const step = every ?? Math.max(1, Math.ceil(data.length / 10));
  const h = hover !== null ? data[hover] : null;
  return (
    <div className="colchart" onMouseLeave={() => setHover(null)}>
      <div className="colchart-plot">
        {[1, 0.5, 0].map((g) => (
          <div key={g} className="grid" style={{ bottom: `${g * 100}%` }}><span>{nf(lang).format(Math.round(max * g))}</span></div>
        ))}
        <div className="bars" role="list">
          {data.map((d, i) => (
            <div key={d.key} role="listitem" tabIndex={0} aria-label={d.tip} className={`cc-bar${hover === i ? ' on' : ''}`}
              onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}>
              <i style={{ height: `${(d.value / max) * 100}%` }} />
            </div>
          ))}
        </div>
        {h && (
          <div className="chart-tip" role="status" style={{ left: `${((hover! + 0.5) / data.length) * 100}%` }}>{h.tip}</div>
        )}
      </div>
      <div className="colchart-x" aria-hidden>
        {data.map((d, i) => <span key={d.key}>{i % step === 0 ? d.label : ''}</span>)}
      </div>
    </div>
  );
}

interface BarRow { key: string; label: string; sub: string; value: number; display: string; extra: string; action?: (() => void) | undefined }

function BarList({ rows, actionLabel }: { rows: BarRow[]; actionLabel?: string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="barlist">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              {r.action ? <button className="linklike ellipsis" onClick={r.action} title={actionLabel}>{r.label}</button> : <div className="ellipsis" style={{ fontWeight: 600 }}>{r.label}</div>}
              {r.sub && <div className="muted small ellipsis">{r.sub}</div>}
            </div>
            <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><div style={{ fontWeight: 650 }}>{r.display}</div><div className="muted small">{r.extra}</div></div>
          </div>
          <div className="track"><i style={{ width: `${(r.value / max) * 100}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}

function DetailTable({ r, lang }: { r: NonNullable<ReturnType<typeof useReport>['data']>; lang: string }) {
  const { t } = useTranslation();
  const [limit, setLimit] = useState(25);
  return (
    <div className="card">
      <h2 className="chart-title">{t('reports.table.title')}</h2>
      <div className="table-scroll">
        <table className="list">
          <thead><tr>
            <th>{t('reports.table.date')}</th><th>{t('reports.table.screen')}</th><th>{t('reports.table.content')}</th><th>{t('reports.table.playlist')}</th>
            <th className="num">{t('reports.table.plays')}</th><th className="num">{t('reports.table.time')}</th>
          </tr></thead>
          <tbody>
            {r.table.slice(0, limit).map((x, i) => (
              <tr key={`${x.date}-${x.deviceId}-${x.contentId ?? i}-${i}`}>
                <td>{dateLabel(x.date, lang)}</td>
                <td><div>{x.deviceName}</div>{x.locationName && <div className="muted small">{x.locationName}</div>}</td>
                <td><div>{x.contentName}</div><div className="muted small">{t(`reports.type.${x.type}`)}</div></td>
                <td>{x.playlistName ?? '–'}</td>
                <td className="num">{nf(lang).format(x.plays)}</td>
                <td className="num">{playTime(x.durationMs, lang)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 10, flexWrap: 'wrap' }}>
        <span className="muted small">{r.tableTruncated ? t('reports.table.truncated', { count: r.table.length }) : t('reports.table.rows', { count: r.table.length })}</span>
        {r.table.length > limit && <button className="btn" onClick={() => setLimit(limit + 200)}>{t('reports.more')}</button>}
      </div>
    </div>
  );
}

// ---------- Kampagnen-Nachweis ----------

function PlaysList({ mediaId, f, lang, rawSince }: { mediaId: string; f: ReportFilter; lang: string; rawSince: string | null }) {
  const { t } = useTranslation();
  const toast = useToast();
  const base = useMemo(() => {
    const o: ReportFilter = { ...f };
    delete o.mediaId;
    return o;
  }, [f]);
  const key = JSON.stringify([mediaId, base]);
  const [items, setItems] = useState<PlayRow[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let off = false;
    setLoading(true);
    getMediaPlays(mediaId, base).then((p) => {
      if (off) return;
      setItems(p.items);
      setNext(p.nextCursor);
    }).catch((e: unknown) => toast.error(e)).finally(() => !off && setLoading(false));
    return () => {
      off = true;
    };
  }, [key]);
  const more = async () => {
    if (!next) return;
    try {
      const p = await getMediaPlays(mediaId, base, next);
      setItems((x) => [...x, ...p.items]);
      setNext(p.nextCursor);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <div className="card">
      <h2 className="chart-title">{t('reports.campaign.plays')}</h2>
      {rawSince && rawSince > f.from && <div className="hint" style={{ marginBottom: 10 }}>{t('reports.campaign.rawSince', { date: dateLong(rawSince, lang) })}</div>}
      {loading ? <Spinner /> : (
        <div className="table-scroll">
          <table className="list" data-testid="plays">
            <thead><tr>
              <th>{t('reports.campaign.when')}</th><th>{t('reports.table.screen')}</th><th>{t('reports.table.playlist')}</th>
              <th className="num">{t('reports.campaign.duration')}</th><th>{t('reports.campaign.end')}</th>
            </tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={`${p.deviceId}-${p.id}`}>
                  <td><div>{localTime(p.startedAt, p.timezone, lang)}</div>{p.compacted && <div className="muted small">{t(`reports.compacted.${p.compacted}`, { count: p.plays })}</div>}</td>
                  <td><div>{p.deviceName}</div>{p.locationName && <div className="muted small">{p.locationName}</div>}</td>
                  <td><div>{p.playlistName ?? '–'}</div>{p.layoutName && <div className="muted small">{p.layoutName}{p.zone ? ` · ${t('reports.campaign.zone', { zone: p.zone })}` : ''}</div>}</td>
                  <td className="num">{playTime(p.durationMs, lang)}</td>
                  <td><span className={`pill plain ${p.endReason === 'completed' ? 'ok' : p.endReason === 'emergency' || p.endReason === 'power_off' ? 'warn' : 'neutral'}`}>{t(`reports.end.${p.endReason}`)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {next && <div className="row" style={{ justifyContent: 'center', marginTop: 10 }}><button className="btn" onClick={() => void more()}>{t('reports.more')}</button></div>}
    </div>
  );
}

// ---------- Einstellungen ----------

const RAW = [3, 6, 12, 13, 24, 36, 60];
const AGG = [12, 24, 36, 60, 120];
const lastMonth = () => presetRange('lastMonth').from.slice(0, 7);

function SettingsTab({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: s, isLoading } = useReportSettings();
  const [f, setF] = useState<{ raw: number; agg: number; enabled: boolean; recipients: string; locale: 'de' | 'en' } | null>(null);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(lastMonth());
  useEffect(() => {
    if (s) setF({ raw: s.rawRetentionMonths, agg: s.aggregateRetentionMonths, enabled: s.monthlyEnabled, recipients: s.monthlyRecipients.join('\n'), locale: s.monthlyLocale });
  }, [s]);
  if (isLoading || !s || !f) return <Spinner />;
  const recipients = f.recipients.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
  const body = { rawRetentionMonths: f.raw, aggregateRetentionMonths: f.agg, monthlyEnabled: f.enabled, monthlyRecipients: recipients, monthlyLocale: f.locale };
  const dirty = JSON.stringify(body) !== JSON.stringify({ rawRetentionMonths: s.rawRetentionMonths, aggregateRetentionMonths: s.aggregateRetentionMonths, monthlyEnabled: s.monthlyEnabled, monthlyRecipients: s.monthlyRecipients, monthlyLocale: s.monthlyLocale });
  const save = async () => {
    setBusy(true);
    try {
      await api.put<ReportSettings>('/reports/settings', body, { ifMatch: s.rowVersion });
      await qc.invalidateQueries({ queryKey: rk.settings });
      toast.show(t('common.saved'));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const sendNow = async () => {
    try {
      const r = await api.post<{ recipients: number }>('/reports/monthly/send', { month });
      toast.show(t('reports.settings.sent', { count: r.recipients }));
    } catch (e) {
      toast.error(e);
    }
  };
  const months = (m: number) => t('reports.settings.months', { count: m });
  return (
    <div className="col" style={{ maxWidth: 760, gap: 16 }}>
      {!canEdit && <div className="hint" role="status">{t('reports.settings.readOnly')}</div>}
      <fieldset className="card col" disabled={!canEdit} style={{ margin: 0 }}>
        <h2 style={{ margin: 0 }}>{t('reports.settings.retention')}</h2>
        <div className="grid-2">
          <Field label={t('reports.settings.raw')} hint={t('reports.settings.rawHint')}>
            <select value={f.raw} onChange={(e) => {
              const raw = Number(e.target.value);
              setF({ ...f, raw, agg: Math.max(f.agg, raw) });
            }}>
              {[...new Set([...RAW, f.raw])].sort((a, b) => a - b).map((m) => <option key={m} value={m}>{months(m)}</option>)}
            </select>
          </Field>
          <Field label={t('reports.settings.agg')} hint={t('reports.settings.aggHint')}>
            <select value={f.agg} onChange={(e) => setF({ ...f, agg: Number(e.target.value) })}>
              {[...new Set([...AGG, f.agg])].sort((a, b) => a - b).filter((m) => m >= f.raw).map((m) => <option key={m} value={m}>{months(m)}</option>)}
            </select>
          </Field>
        </div>
      </fieldset>
      <fieldset className="card col" disabled={!canEdit} style={{ margin: 0 }}>
        <h2 style={{ margin: 0 }}>{t('reports.settings.monthly')}</h2>
        {!s.smtpConfigured && <div className="hint warn" role="status">{t('reports.settings.noSmtp')}</div>}
        <label className="check"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} />{t('reports.settings.monthlyOn')}</label>
        <Field label={t('reports.settings.recipients')} hint={t('reports.settings.recipientsHint')}>
          <textarea rows={3} value={f.recipients} onChange={(e) => setF({ ...f, recipients: e.target.value })} placeholder="marketing@firma.ch" />
        </Field>
        <Field label={t('reports.settings.language')}>
          <select value={f.locale} onChange={(e) => setF({ ...f, locale: e.target.value as 'de' | 'en' })}><option value="de">Deutsch</option><option value="en">English</option></select>
        </Field>
        <div className="small muted">{t('reports.settings.when', { tz: s.timezone })}</div>
      </fieldset>
      {canEdit && (
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <button className="btn primary" onClick={() => void save()} disabled={busy || !dirty || (f.enabled && !recipients.length)}><Icon name="check" />{t('common.save')}</button>
          <input type="month" aria-label={t('reports.settings.month')} value={month} onChange={(e) => setMonth(e.target.value)} />
          <button className="btn" onClick={() => void sendNow()} disabled={!s.smtpConfigured || !s.monthlyRecipients.length || dirty} title={dirty ? t('reports.settings.saveFirst') : ''}>
            <Icon name="send" />{t('reports.settings.sendNow')}
          </button>
        </div>
      )}
    </div>
  );
}
